import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  ArrowLeft, FileText, Plus, Loader2, Save, Search, Car, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VehiculePicker from "@/components/VehiculePicker";
import ClientSelectedCard from "@/components/ClientSelectedCard";
import OrReferencesBlock from "@/components/or/OrReferencesBlock";
import QuickVehiculeDialog from "@/components/QuickVehiculeDialog";
import QuickClientDialog from "@/components/QuickClientDialog";
import LignesEditor from "@/components/LignesEditor";
import { useUnsavedChangesGuard, useSafeNavigate } from "@/components/UnsavedChangesGuard";

export default function FactureCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilled = location.state || {};
  const [submitting, setSubmitting] = useState(false);

  // Client picker
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [client, setClient] = useState(prefilled.client || null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Véhicule (optionnel)
  const [vehicules, setVehicules] = useState([]);
  const [vehiculeId, setVehiculeId] = useState(prefilled.vehiculeId || "");
  const [quickVehiculeOpen, setQuickVehiculeOpen] = useState(false);
  // iter122 — Ajout d'un client à la volée
  const [quickClientOpen, setQuickClientOpen] = useState(false);

  const searchClients = useCallback(async (q) => {
    if (!q || q.length < 1) { setClientResults([]); return; }
    try {
      const { data } = await api.get("/clients", { params: { q, limit: 8 } });
      setClientResults(data.items || []);
    } catch (e) { setClientResults([]); }
  }, []);
  useEffect(() => {
    const t = setTimeout(() => searchClients(clientQuery), 200);
    return () => clearTimeout(t);
  }, [clientQuery, searchClients]);

  // Charge les véhicules du client choisi + auto-sélection du véhicule principal
  useEffect(() => {
    if (!client?.id) { setVehicules([]); setVehiculeId(""); return; }
    api.get(`/clients/${client.id}`)
      .then(({ data }) => {
        const vlist = data.vehicules || [];
        setVehicules(vlist);
        if (!vehiculeId) {
          const principal = vlist.find((v) => v.est_principal);
          if (principal) setVehiculeId(principal.id);
        }
      })
      .catch(() => setVehicules([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  // Form
  const [dateEm, setDateEm] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateEch, setDateEch] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [references, setReferences] = useState([]);   // iter241 — en-têtes/références
  const [piedLibre, setPiedLibre] = useState("");
  const [lignes, setLignes] = useState([]);
  const [rabaisGlobal, setRabaisGlobal] = useState({ pct: 0, chf: 0 });

  // Détection des modifications par rapport à un brouillon vide initial
  const isDirty = useMemo(() => {
    if (client) return true;
    if (vehiculeId) return true;
    if (piedLibre) return true;
    if (references.length > 0) return true;
    if (lignes.length > 0) return true;
    if (rabaisGlobal.pct > 0 || rabaisGlobal.chf > 0) return true;
    return false;
  }, [client, vehiculeId, piedLibre, references, lignes, rabaisGlobal]);

  // submitted=true après création réussie → désactive le guard pendant navigate()
  const [submitted, setSubmitted] = useState(false);
  useUnsavedChangesGuard(isDirty && !submitted, async () => {
    return await submit();
  });
  const safeNavigate = useSafeNavigate();

  const submit = async () => {
    if (!client) { toast.error("Sélectionnez un client"); return false; }
    const valides = lignes.filter((l) => l.libelle && l.libelle.trim() && (parseFloat(l.prix_unitaire_ht) > 0 || parseFloat(l.quantite) > 0));
    if (valides.length === 0) { toast.error("Au moins une ligne valide est requise"); return false; }
    setSubmitting(true);
    try {
      const payload = {
        client_id: client.id,
        vehicule_id: vehiculeId || null,
        date_emission: dateEm,
        date_echeance: dateEch || null,
        rabais_global_pct: parseFloat(rabaisGlobal.pct) || 0,
        rabais_global_chf: parseFloat(rabaisGlobal.chf) || 0,
        references,
        pied_libre: piedLibre || null,
        lignes: valides.map((l) => ({
          libelle: l.libelle,
          quantite: parseFloat(l.quantite) || 1,
          prix_unitaire_ht: parseFloat(l.prix_unitaire_ht) || 0,
          tva_taux: parseFloat(l.tva_taux) || 0,
          unite: l.unite || null,
          rabais_pct: parseFloat(l.rabais_pct) || 0,
          rabais_chf: parseFloat(l.rabais_chf) || 0,
          type_ligne: l.type_ligne || "libre",
          article_id: l.article_id || null,
        })),
      };
      const { data } = await api.post("/factures", payload);
      toast.success(`Facture ${data.numero} créée`);
      setSubmitted(true);
      navigate(`/facturation/${data.id}`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="facture-create-page">
      <button onClick={() => navigate("/facturation")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-6">
        <FileText className="w-7 h-7 text-[#52a675]" /> Nouvelle facture
      </h1>

      {/* Client */}
      <Card className="p-5 mb-4 border-gray-200">
        <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Client *</Label>
        {client ? (
          <ClientSelectedCard
            client={client}
            onClear={() => { setClient(null); setClientQuery(""); setVehiculeId(""); setVehicules([]); }}
            testIdPrefix="facture-client"
          />
        ) : (
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={clientQuery}
                  onChange={(e) => { setClientQuery(e.target.value); setPickerOpen(true); }}
                  onFocus={() => setPickerOpen(true)}
                  placeholder="Rechercher un client..."
                  className="pl-10"
                  data-testid="facture-client-search"
                />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
              <div className="max-h-64 overflow-y-auto">
                {clientResults.length === 0 && <div className="p-3 text-xs text-gray-400">Tapez pour rechercher...</div>}
                {clientResults.map((c) => {
                  const name = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                  return (
                    <button
                      key={c.id}
                      onClick={() => { setClient(c); setPickerOpen(false); setClientQuery(""); }}
                      className="w-full text-left p-2.5 hover:bg-gray-50 border-b border-gray-100"
                      data-testid={`facture-client-pick-${c.id}`}
                    >
                      <div className="font-semibold text-sm">{name}</div>
                      <div className="text-xs text-gray-500">{c.localite} · {c.tel_principal || c.email || ""}</div>
                    </button>
                  );
                })}
                {/* iter122 — Bouton + Nouveau client si pas trouvé */}
                <button
                  onClick={() => { setPickerOpen(false); setQuickClientOpen(true); }}
                  className="w-full text-left p-2.5 hover:bg-emerald-50 border-t border-gray-200 flex items-center gap-2 text-[#52a675] font-medium"
                  data-testid="facture-quick-client-add"
                >
                  <Plus className="w-4 h-4" />
                  Nouveau client {clientQuery ? `« ${clientQuery} »` : ""}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </Card>

      {/* Véhicule (optionnel) */}
      {client && (
        <Card className="p-5 mb-4 border-gray-200" data-testid="facture-vehicule-section">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
              Véhicule <span className="text-gray-400 normal-case">(optionnel)</span>
            </Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setQuickVehiculeOpen(true)}
              data-testid="facture-add-vehicule"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un véhicule
            </Button>
          </div>
          {vehicules.length > 0 ? (
            <div className="flex items-start gap-2">
              <Car className="w-4 h-4 text-gray-400 flex-shrink-0 mt-2.5" />
              <div className="flex-1 min-w-0">
                <VehiculePicker
                  vehicules={vehicules}
                  value={vehiculeId}
                  onChange={setVehiculeId}
                  allowNone
                  noneLabel="Aucun véhicule"
                  testId="facture-vehicule-select"
                  showDetails
                  onVehiculeUpdated={(v) => setVehicules((prev) => prev.map((x) => (x.id === v.id ? { ...x, ...v } : x)))}
                />
              </div>
              {vehiculeId && (
                <Button size="icon" variant="ghost" onClick={() => setVehiculeId("")} title="Retirer le véhicule" data-testid="facture-vehicule-clear">
                  <X className="w-4 h-4 text-gray-400" />
                </Button>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">
              Ce client n'a aucun véhicule enregistré. Cliquez sur « Ajouter un véhicule » si nécessaire.
            </div>
          )}
        </Card>
      )}

      {/* Métadonnées */}
      <Card className="p-5 mb-4 border-gray-200">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Date d'émission *</Label>
            <Input type="date" value={dateEm} onChange={(e) => setDateEm(e.target.value)} data-testid="facture-date-emission" />
          </div>
          <div>
            <Label>Échéance (30j auto)</Label>
            <Input type="date" value={dateEch} onChange={(e) => setDateEch(e.target.value)} data-testid="facture-date-echeance" />
          </div>
        </div>
      </Card>

      {/* iter241 — En-têtes / références, harmonisé avec les OR */}
      <Card className="p-5 mb-4 border-gray-200">
        <OrReferencesBlock value={references} onChange={setReferences} />
      </Card>

      {/* Lignes — composant partagé */}
      <Card className="p-5 mb-4 border-gray-200">
        <h3 className="font-heading font-semibold text-gray-900 mb-3">Lignes</h3>
        <LignesEditor
          lignes={lignes}
          setLignes={setLignes}
          rabaisGlobal={rabaisGlobal}
          setRabaisGlobal={setRabaisGlobal}
          tvaDefaut={8.1}
        />
      </Card>

      {/* Pied de page libre (bas du PDF) */}
      <Card className="p-5 mb-4 border-gray-200">
        <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
          Texte de pied de page <span className="text-gray-400 normal-case">(bas du PDF, après totaux)</span>
        </Label>
        <Textarea
          value={piedLibre}
          onChange={(e) => setPiedLibre(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Ex : Modalités de paiement particulières, mentions légales additionnelles…"
          className="mt-2"
          data-testid="facture-pied-libre"
        />
      </Card>

      <div className="flex items-center justify-between gap-2 flex-wrap pb-8">
        <p className="text-xs text-gray-500">
          ℹ️ Les écritures comptables (1100 Créances → 3200/3400 Ventes + 2200 TVA due) seront générées automatiquement.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => safeNavigate("/facturation")}
            data-testid="facture-cancel"
          >
            <X className="w-4 h-4 mr-2" /> Annuler
          </Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="facture-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer la facture
          </Button>
        </div>
      </div>

      {/* Bouton "Retour" en haut */}

      {/* QuickVehiculeDialog : création rapide d'un véhicule pour ce client */}
      <QuickVehiculeDialog
        open={quickVehiculeOpen}
        onClose={() => setQuickVehiculeOpen(false)}
        clientId={client?.id}
        clientNom={client?.raison_sociale || `${client?.prenom || ""} ${client?.nom || ""}`.trim()}
        onCreated={(v) => {
          setVehicules((prev) => [...prev, v]);
          setVehiculeId(v.id);
          setQuickVehiculeOpen(false);
        }}
      />
      {/* iter122 — QuickClientDialog déclenché depuis le picker */}
      <QuickClientDialog
        open={quickClientOpen}
        onClose={() => setQuickClientOpen(false)}
        prefillSearch={clientQuery}
        source="facture_create"
        onCreated={(c) => {
          setClient(c);
          setClientQuery("");
          setQuickClientOpen(false);
        }}
      />
    </div>
  );
}
